import cn from 'classnames';
import { forwardRef, useRef } from 'react';
import { mergeRefs } from 'react-merge-refs';

import { CloseIcon } from '../../../foundations/icon';

import type { ForwardedRef, KeyboardEventHandler, MouseEvent, ReactElement } from 'react';

import type { TagProps } from './tag.types';

import './tag.scss';

/**
 * @internal
 * The base tag component.
 * Should be used only by other tag components and not exposed to the end users.
 *
 * @description
 * Tags are interactive labels used to visually group, classify, or filter criteria.
 */
const Tag = forwardRef((props: TagProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
  const {
    bordered = false,
    children,
    className,
    disabled = false,
    dismissible = false,
    icon,
    onClick,
    onDismiss,
    readOnly = false,
    ...rest
  } = props;

  const tagRef = useRef<HTMLElement>(null);
  const handleRef = mergeRefs([tagRef, forwardedRef]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    event?.preventDefault();

    if (disabled) {
      return;
    }
    onClick?.(event);
  };

  const handleonDismiss = (event: MouseEvent<HTMLButtonElement>): void => {
    if (disabled || !dismissible) {
      return;
    }
    onDismiss?.(event);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    (rest as React.HTMLAttributes<HTMLDivElement>)?.onKeyDown?.(event);

    const { key } = event;
    switch (key) {
      case ' ':
      case 'Enter': {
        if (disabled) {
          return;
        }
        onClick?.(event);

        break;
      }
      case 'Backspace':
      case 'Delete': {
        if (disabled || !dismissible) {
          return;
        }
        onDismiss?.(event);

        break;
      }
      case 'Escape': {
        tagRef.current?.blur();

        break;
      }
      // No default
    }
  };

  const classes = cn('rbui-tag', className);

  const contentClasses = cn('rbui-tag__content', 'rbui-button-reset', {
    'rbui-tag__content--bordered': bordered,
    'rbui-tag__content--disabled': disabled,
    'rbui-tag__content--dismissible': dismissible,
    'rbui-tag__content--read-only': readOnly,
  });

  const closeWrapperClasses = cn('rbui-tag__close-wrapper', 'rbui-button-reset', {
    'rbui-tag__close-wrapper--bordered': bordered,
    'rbui-tag__close-wrapper--disabled': disabled,
  });

  return (
    <div
      {...rest}
      className={classes}
      onKeyDown={handleKeyDown}
      ref={handleRef}
      role="button"
      tabIndex={disabled ? -1 : 0}
    >
      <button
        className={contentClasses}
        onClick={handleClick}
        // Internal elements are not focusable
        tabIndex={-1}
        type="button"
      >
        {icon && (
          <span className="rbui-tag__content-icon" role="img">
            {icon}
          </span>
        )}

        {children && (
          <span className="rbui-tag__content-text" role="banner">
            {children}
          </span>
        )}
      </button>

      {dismissible && (
        <button
          aria-disabled={disabled}
          aria-label="Dismiss"
          className={closeWrapperClasses}
          onClick={handleonDismiss}
          tabIndex={-1}
          type="button"
        >
          <CloseIcon className="rbui-tag__close-wrapper-icon" />
        </button>
      )}
    </div>
  );
});

Tag.displayName = 'Tag';

export default Tag;
